import React, { useEffect, useState } from "react";
import { Download, ArrowLeft, Loader2, FileText } from "lucide-react";
import api, { API } from "../lib/api";
import Flipbook from "../components/Flipbook";
import { journey, visitorId } from "../lib/journey";

const G = "#C88A24", IVORY = "#F3EFE7", MUTED = "#B7B0A5";

export default function BlueprintReader() {
    const [state, setState] = useState("loading");
    const [info, setInfo] = useState(null);
    const [pages, setPages] = useState([]);
    const [lastPage, setLastPage] = useState(0);
    const [resumed, setResumed] = useState(false);
    // 3 Nov 2026 (RK): "Back" takes the reader to the video course offer (when it is switched on), or to their course if they own it
    const [backTo, setBackTo] = useState("/blueprint");
    const token = new URLSearchParams(window.location.search).get("token");
    const pdfBase = `${API}/blueprint/pdf/${token}`;
    const saveTimer = React.useRef(null);

    useEffect(() => {
        if (!token) { setState("denied"); return; }
        (async () => {
            try {
                const acc = await api.get(`/blueprint/access/${token}`, { params: { vid: visitorId() } });
                setInfo(acc.data);
            } catch {
                setState("denied");
                return;
            }
            try {
                const pg = await api.get(`/blueprint/pages/${token}`);
                setPages(pg.data.pages || []);
                setLastPage(pg.data.last_page || 0);
            } catch { /* reader render failed — PDF download still offered below */ }
            setState("ok");
            api.get("/blueprint/upsell", { params: { token, peek: 1 } }).then((r) => {
                if (r.data?.enrolled && r.data.learn_path) setBackTo(r.data.learn_path);
                else if (r.data?.show) setBackTo(`/blueprint/course?token=${encodeURIComponent(token)}`);
            }).catch(() => {});
        })();
    }, [token]);

    const maxPage = React.useRef(0);
    const savePage = React.useCallback((page) => {
        if (page > 0) setResumed(false);
        if (page > maxPage.current) { maxPage.current = page; journey("ebook_page", String(page + 1)); } // furthest page reached
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
            api.post(`/blueprint/bookmark/${token}`, { page }).catch(() => {});
        }, 900);
    }, [token]);

    useEffect(() => { if (lastPage > 0) setResumed(true); }, [lastPage]);

    if (state === "loading") return (
        <div className="min-h-screen grid place-items-center bg-[#080808] text-center px-6" style={{ color: G }}>
            <div>
                <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                <p className="mt-4 text-xs tracking-[0.3em]" style={{ color: MUTED }}>PREPARING YOUR BOOK…</p>
            </div>
        </div>
    );

    if (state === "denied") return (
        <div className="min-h-screen grid place-items-center bg-[#080808] text-center px-6" style={{ color: IVORY }}>
            <div><p className="text-xs tracking-[0.35em] mb-4" style={{ color: G }}>ACCESS REQUIRED</p><h1 className="font-display font-black text-3xl sm:text-4xl">This reader is for buyers</h1><p className="mt-3" style={{ color: MUTED }}>We couldn't verify your access link.</p><a href="/blueprint" className="mt-6 inline-flex items-center gap-2 px-6 py-3 font-bold rounded-sm" style={{ background: G, color: "#080808" }}>Get The Blueprint</a></div>
        </div>
    );

    return (
        <div className="min-h-screen flex flex-col bg-[#080808]" style={{ color: IVORY, backgroundImage: "radial-gradient(120% 80% at 50% 0%, #1c130a 0%, #080808 60%)" }} data-testid="blueprint-reader">
            <header className="flex items-center justify-between gap-3 px-4 sm:px-8 h-16 border-b border-[#C88A24]/20 sticky top-0 bg-[#080808]/90 backdrop-blur z-10">
                <a href={backTo} className="inline-flex items-center gap-2 text-sm whitespace-nowrap" style={{ color: MUTED }} data-testid="bp-reader-back"><ArrowLeft className="h-4 w-4" /> {backTo === "/blueprint" ? "Back" : backTo.startsWith("/learn") ? "My course" : "Video course"}</a>
                <p className="font-display font-black text-sm sm:text-base truncate">RK <span style={{ color: G }}>×</span> MuviArts · The Blueprint</p>
                <a href={`${pdfBase}?download=1`} className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-sm" style={{ background: G, color: "#080808" }} data-testid="bp-download-pdf" data-track="Download ebook PDF" onClick={() => journey("ebook_download")}><Download className="h-4 w-4" /> PDF</a>
            </header>
            <div className="text-center px-4 pt-4 text-sm" style={{ color: MUTED }}>
                {resumed
                    ? <>Welcome back{info?.name ? `, ${info.name}` : ""} — resuming where you left off (page {lastPage + 1}).</>
                    : <>You're in{info?.name ? `, ${info.name}` : ""} — enjoy the read.</>}
            </div>
            <div className="flex-1 flex items-center justify-center px-2 sm:px-6 py-6">
                {pages.length ? <Flipbook pages={pages} initialPage={lastPage} onPageChange={savePage} /> : (
                    <div className="text-center" style={{ color: MUTED }}>
                        <p>Reader unavailable. You can still download the PDF above.</p>
                    </div>
                )}
            </div>
            {info?.kit?.length > 0 && (
                <section className="px-4 sm:px-8 pb-12" data-testid="bp-release-kit">
                    <div className="max-w-3xl mx-auto rounded-2xl p-5 sm:p-7" style={{ border: "1px solid rgba(200,138,36,.35)", background: "#120d08" }}>
                        <p className="text-[11px] tracking-[0.3em] text-center sm:text-left" style={{ color: G }}>YOUR RELEASE KIT</p>
                        <h2 className="mt-1 font-display font-black text-xl sm:text-2xl text-center sm:text-left">Templates for your next release</h2>
                        <div className="mt-4 grid sm:grid-cols-2 gap-2">
                            {[...info.kit].sort((x, y) => (y.file.startsWith("Release Kit") ? 1 : 0) - (x.file.startsWith("Release Kit") ? 1 : 0)).map((k) => (
                                <a key={k.file} href={`${API}/blueprint/kit/${token}/${encodeURIComponent(k.file)}`} className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm hover:bg-[#1a120a]" style={{ border: "1px solid rgba(255,255,255,.08)" }} data-testid="bp-kit-file">
                                    {k.file.startsWith("Release Kit") ? <Download className="h-4 w-4 shrink-0" style={{ color: G }} /> : <FileText className="h-4 w-4 shrink-0" style={{ color: G }} />}
                                    <span className={k.file.startsWith("Release Kit") ? "font-bold" : ""}>{k.file.startsWith("Release Kit") ? "The Release Kit (full designed PDF)" : `${k.name} · printable`}</span>
                                </a>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
